/**
 * ADR-118 — permission-check hooks.
 *
 * Reads from the user object exposed by `@aegix/auth-identity`. The
 * user shape must extend `WithPermissions` (carry a
 * ``permissions: readonly string[]`` field). The hooks throw a
 * helpful error rather than returning `false` for everything when
 * mounted against a permissionless user shape — silent false-y
 * answers would hide a wiring bug.
 */

import { useMemo } from 'react';
import type { BaseUser } from '@aegix/auth-core';
import { useUser } from '@aegix/auth-identity';
import type { WithPermissions } from './types';

/**
 * Returns the current user's permissions array as a readonly tuple.
 *
 * - Anonymous / logged-out / authenticating → empty array.
 * - Authenticated / refreshing / expired with a user that has
 *   `permissions: string[]` → that array.
 * - Authenticated user without a `permissions` field → throws a
 *   helpful error pointing at the wiring bug. This is intentional:
 *   silent `[]` would let `useHasPermission` lie everywhere.
 *
 * The returned array is referentially stable as long as the user
 * object hasn't changed (the hook does no copying — it returns the
 * underlying array directly).
 */
export function usePermissions(): readonly string[] {
  const user = useUser<BaseUser & WithPermissions>();
  if (!user) return EMPTY_PERMS;

  // The TS generic above keeps consumer code honest at compile time,
  // but at runtime an app may mount this hook in a context where the
  // adapter didn't include `permissions`. Detect that and fail loudly
  // — a silent `[]` here would silently disable every perm-gated UI
  // element and the cause would be impossible to find from a Sentry
  // crumb.
  if (!Array.isArray((user as Partial<WithPermissions>).permissions)) {
    throw new Error(
      '[auth-permissions] usePermissions/useHasPermission requires the ' +
        "user shape to include a `permissions: string[]` field. The " +
        'current user has no such field. Either:\n' +
        '  (a) update your login adapter to include permissions in the ' +
        'user it returns (see SimpleJwtUser in @aegix/auth-identity), or\n' +
        '  (b) stop mounting auth-permissions hooks in this app (ONE ' +
        'consumers do not need this lib).',
    );
  }

  return user.permissions;
}

/**
 * O(1) permission check. Memoizes a `Set` from the permissions array
 * so consecutive calls in the same render — and across renders if
 * `permissions` is referentially stable — are cheap.
 *
 * Matches the wire format `app_label.codename` (e.g.
 * `'schools.change_alerttype'`). Returns `false` for an anonymous
 * user (does NOT throw — anon-checking is a common gate pattern).
 */
export function useHasPermission(codename: string): boolean {
  const permissions = usePermissions();
  const set = useMemo(() => new Set(permissions), [permissions]);
  return set.has(codename);
}

const EMPTY_PERMS: readonly string[] = Object.freeze([]);
